import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { dbPayload } from "../../../auth/registered/SEC_00_DB_Payload";

const RELATIONSHIP_TYPE_SUPPLIER = "SUPPLIER";

/**
 * 🛰️ FETCH SUPPLIERS
 * Active Corp.Z_01_07_CorporateEntity_Relationship rows for one client
 * CorporateEntity, filtered to RelationshipTypeCode = SUPPLIER.
 * Targets: RepresentativeController.relationship-get (Corp.usp_Z_01_07_Relationship_Get_ByClient)
 */
export const fetchSuppliers = async (clientEntityId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/representative/relationship-get",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({
                ClientEntityId: clientEntityId,
                RelationshipTypeCode: RELATIONSHIP_TYPE_SUPPLIER,
            })
        );

        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Supplier Fetch Failed:", err);
        return [];
    }
};

/**
 * 🛰️ CREATE SUPPLIER (single hit): Name + Reg Number + registered Address
 * (Line1/2, Town, County, Postcode, Country), submitted together — mirrors
 * representativeInteractionService.registerCompanyFull's combined shape.
 * Creates the supplier's CorporateEntity + its Address row + the
 * Corp.Z_01_07_CorporateEntity_Relationship link (RelationshipTypeCode =
 * SUPPLIER) in one call. Unlike Register Company, this never grants
 * Representative access. Returns { CorporateEntityId, EntityName, relationshipId }.
 * Targets: RepresentativeController.create-supplier-full
 */
export const createSupplierWithAddress = async (clientEntityId, supplier, config) => {
    try {
        const payload = {
            ClientEntityId: clientEntityId,
            EntityName: supplier.name,
            TaxReference: supplier.regNumber,
            Country_ISO2: supplier.country,
            AddressLine1: supplier.addressLine1,
            AddressLine2: supplier.addressLine2 || null,
            Town: supplier.town,
            County: supplier.county || null,
            Postcode: supplier.postcode,
            AddressSourceCode: supplier.addressSourceCode || null,
        };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/create-supplier-full",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Supplier creation failed.");
        return res.data;
    } catch (err) {
        console.error("Supplier Create Error:", err);
        throw err;
    }
};

/**
 * 🛰️ LINK EXISTING SUPPLIER
 * Links an already-registered CorporateEntity (e.g. also a Sustina client) to
 * clientEntityId as a SUPPLIER, without creating a new entity/address.
 * Targets: RepresentativeController.relationship-create (Corp.usp_Z_01_07_Relationship_Create)
 */
export const createSupplierRelationship = async (clientEntityId, supplierEntityId, config) => {
    try {
        const payload = {
            ClientEntityId: clientEntityId,
            CounterpartyEntityId: supplierEntityId,
            RelationshipTypeCode: RELATIONSHIP_TYPE_SUPPLIER,
        };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/relationship-create",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Supplier link failed.");
        return res.data;
    } catch (err) {
        console.error("Supplier Link Error:", err);
        throw err;
    }
};

/**
 * 🛰️ REVOKE SUPPLIER RELATIONSHIP
 * Targets: RepresentativeController.relationship-revoke (Corp.usp_Z_01_07_Relationship_Revoke)
 */
export const revokeSupplierRelationship = async (clientEntityId, supplierEntityId, config) => {
    try {
        const payload = {
            ClientEntityId: clientEntityId,
            CounterpartyEntityId: supplierEntityId,
            RelationshipTypeCode: RELATIONSHIP_TYPE_SUPPLIER,
        };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/relationship-revoke",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Supplier revoke failed.");
        return true;
    } catch (err) {
        console.error("Supplier Revoke Error:", err);
        throw err;
    }
};

/**
 * 🛰️ REACTIVATE SUPPLIER RELATIONSHIP: undoes a prior Remove — never a delete
 * either way, just IsActive flipping back to 1.
 * Targets: RepresentativeController.relationship-reactivate (Corp.usp_Z_01_07_Relationship_Reactivate)
 */
export const reactivateSupplierRelationship = async (clientEntityId, supplierEntityId, config) => {
    try {
        const payload = {
            ClientEntityId: clientEntityId,
            CounterpartyEntityId: supplierEntityId,
            RelationshipTypeCode: RELATIONSHIP_TYPE_SUPPLIER,
        };

        const rawData = await dbPayload(
            "/api/v1/sustina/representative/relationship-reactivate",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi(payload)
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Supplier reactivate failed.");
        return true;
    } catch (err) {
        console.error("Supplier Reactivate Error:", err);
        throw err;
    }
};

/**
 * 🛰️ CLASSIFY SUPPLIER: assigns one ESG taxonomy concept (Ref.ESG_TaxonomyDetails.RowId)
 * to a relationship. Call once per concept — a supplier can be classified
 * against several at once.
 * Targets: RelationshipClassificationController.assign
 */
export const assignSupplierClassification = async (relationshipId, taxonomyDetailRowId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/relationship-classification/assign",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ RelationshipId: relationshipId, TaxonomyDetailRowId: taxonomyDetailRowId })
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Supplier classification failed.");
        return res.data;
    } catch (err) {
        console.error("Supplier Classification Assign Error:", err);
        throw err;
    }
};

/**
 * 🛰️ FETCH SUPPLIER CLASSIFICATIONS: every ESG concept currently assigned to a relationship.
 * Targets: RelationshipClassificationController.get-by-relationship
 */
export const fetchSupplierClassifications = async (relationshipId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/relationship-classification/get-by-relationship",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ RelationshipId: relationshipId })
        );

        const res = GoldenSourceHelper.normalize(rawData);
        return res.success ? res.data : [];
    } catch (err) {
        console.error("Supplier Classification Fetch Failed:", err);
        return [];
    }
};

/**
 * 🛰️ REVOKE SUPPLIER CLASSIFICATION
 * Targets: RelationshipClassificationController.revoke
 */
export const revokeSupplierClassification = async (relationshipId, taxonomyDetailRowId, config) => {
    try {
        const rawData = await dbPayload(
            "/api/v1/sustina/relationship-classification/revoke",
            "POST",
            config.siteKey,
            GoldenSourceHelper.prepareForApi({ RelationshipId: relationshipId, TaxonomyDetailRowId: taxonomyDetailRowId })
        );

        const res = GoldenSourceHelper.normalize(rawData);
        if (!res.success) throw new Error(res.message || "Supplier classification revoke failed.");
        return true;
    } catch (err) {
        console.error("Supplier Classification Revoke Error:", err);
        throw err;
    }
};
